import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const STATUS_COLORS = {
  PENDING: '#6b7280',
  MATCHED: '#3b82f6',
  APPROVED: '#f59e0b',
  MIGRATED: '#10b981',
  REJECTED: '#ef4444',
  FAILED: '#dc2626',
};

export default function MigrationReview() {
  const [records, setRecords] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('');
  const [practiceFilter, setPracticeFilter] = useState('');
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [remerging, setRemerging] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [scanResult, setScanResult] = useState(null);
  const [executeResult, setExecuteResult] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status = filter;
      if (practiceFilter) params.practiceId = practiceFilter;
      // Counts must mirror the list filter (practice + default-hide-past),
      // otherwise the banner shows totals for one scope and the table shows
      // another — confusing on cutover day.
      const countsParams = {};
      if (practiceFilter) countsParams.practiceId = practiceFilter;
      const [listRes, countsRes] = await Promise.all([
        api.migrationList(params),
        api.migrationCounts(countsParams)
      ]);
      if (listRes.success) setRecords(listRes.data.records || []);
      if (countsRes.success) setCounts(countsRes.data || {});
    } catch (err) {
      console.error('Failed to load migration data:', err);
    }
    setLoading(false);
  }, [filter, practiceFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    api.request('/practices')
      .then(res => { if (res.success) setPractices(res.data || []); })
      .catch(err => console.error('Failed to load practices:', err));
  }, []);

  const handleScan = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      // GH-126: scan is now async on the backend. POST returns 202 + jobId
      // instantly, we poll GET /scan/:jobId until status !== 'running'.
      // Solves the ERR_CONNECTION_CLOSED failures where the backend was
      // succeeding silently but the browser lost the connection during the
      // ~80s round-trip.
      const kick = await api.migrationScan();
      if (!kick.success || !kick.data?.jobId) {
        setScanResult({ error: kick.error || 'Failed to start scan' });
        setScanning(false);
        return;
      }
      const jobId = kick.data.jobId;
      setScanResult({ status: 'running', jobId });

      const POLL_MS = 3000;
      const MAX_TICKS = 200; // ~10 minutes cap
      let ticks = 0;
      while (ticks < MAX_TICKS) {
        await new Promise(r => setTimeout(r, POLL_MS));
        ticks++;
        try {
          const status = await api.request(`/migration/scan/${jobId}`);
          if (!status.success) {
            setScanResult({ error: status.error || 'Poll failed' });
            break;
          }
          if (status.data.status === 'complete') {
            setScanResult(status.data.result);
            await loadData();
            break;
          }
          if (status.data.status === 'error') {
            setScanResult({ error: status.data.error || 'Scan failed on server' });
            break;
          }
          // still 'running' — update banner with elapsed time so users see progress
          setScanResult({ status: 'running', jobId, elapsedSeconds: ticks * (POLL_MS / 1000) });
        } catch (pollErr) {
          // Transient network hiccup — keep polling instead of failing hard
          console.warn('[Migration] scan poll error, retrying:', pollErr.message);
        }
      }
      if (ticks >= MAX_TICKS) {
        setScanResult({ error: 'Scan is still running after 10 minutes — check backend logs' });
      }
    } catch (err) {
      setScanResult({ error: err.message });
    }
    setScanning(false);
  };

  const handleApprove = async () => {
    if (selected.size === 0) return;
    // Only MATCHED rows can be approved — PENDING rows are unmatched and would
    // 100% fail at execute with "No V2 form found for this appointment". Block
    // here with a clear message instead of letting the user discover it later.
    const selectedRecords = records.filter(r => selected.has(r.id));
    const unmatched = selectedRecords.filter(r => r.status !== 'MATCHED');
    if (unmatched.length > 0) {
      alert(`Cannot approve ${unmatched.length} unmatched record${unmatched.length === 1 ? '' : 's'}.\n\nUnmatched (PENDING) rows have no V2 patient/appointment to migrate into. Use "Manual Match" first, or "Reject Selected" if the form shouldn't migrate.`);
      return;
    }
    try {
      await api.migrationApprove([...selected]);
      setSelected(new Set());
      await loadData();
    } catch (err) {
      console.error('Approve failed:', err);
      alert('Approve failed: ' + (err.message || 'unknown error'));
    }
  };

  const handleReject = async () => {
    if (selected.size === 0) return;
    try {
      await api.migrationReject([...selected]);
      setSelected(new Set());
      await loadData();
    } catch (err) {
      console.error('Reject failed:', err);
    }
  };

  const handleExecute = async () => {
    setExecuting(true);
    setExecuteResult(null);
    try {
      // Fetch the canonical list of APPROVED IDs (respects practice filter so
      // a per-practice cutover only migrates that practice's approved forms).
      // Don't read from `records` — that's filtered to the current view and may
      // omit APPROVED rows when the user has a different status filter active.
      const params = { status: 'APPROVED' };
      if (practiceFilter) params.practiceId = practiceFilter;
      const listRes = await api.migrationList(params);
      const approvedIds = (listRes.success ? listRes.data.records : []).map(r => r.id);

      if (approvedIds.length === 0) {
        setExecuteResult({ error: practiceFilter
          ? 'No approved records for the selected practice'
          : 'No approved records to migrate' });
        setExecuting(false);
        return;
      }

      const res = await api.migrationExecute(approvedIds);
      setExecuteResult(res.success ? res.data : { error: res.error });
      await loadData();
    } catch (err) {
      setExecuteResult({ error: err.message });
    }
    setExecuting(false);
  };

  const handleRetry = async () => {
    setRetrying(true);
    setExecuteResult(null);
    try {
      // Use the selection if any FAILED rows are checked; otherwise retry ALL
      // failed rows in the current practice scope. Matches the existing pattern
      // where Migrate Approved fans out across the practice filter.
      const selectedFailed = records.filter(r => selected.has(r.id) && r.status === 'FAILED').map(r => r.id);
      let failedIds = selectedFailed;
      if (failedIds.length === 0) {
        const params = { status: 'FAILED' };
        if (practiceFilter) params.practiceId = practiceFilter;
        const listRes = await api.migrationList(params);
        failedIds = (listRes.success ? listRes.data.records : []).map(r => r.id);
      }
      if (failedIds.length === 0) {
        setExecuteResult({ error: practiceFilter
          ? 'No failed records for the selected practice'
          : 'No failed records to retry' });
        setRetrying(false);
        return;
      }
      const res = await api.migrationRetry(failedIds);
      setExecuteResult(res.success ? res.data : { error: res.error });
      setSelected(new Set());
      await loadData();
    } catch (err) {
      setExecuteResult({ error: err.message });
    }
    setRetrying(false);
  };

  const handleRemerge = async () => {
    // Re-runs the merge step against existing V2 forms. Destructive when V1
    // and the V2 form have different answers for the same question — V1
    // wins. Confirm with the admin to avoid surprises.
    const selectedMigrated = records.filter(r => selected.has(r.id) && r.status === 'MIGRATED').map(r => r.id);
    let migratedIds = selectedMigrated;
    if (migratedIds.length === 0) {
      const params = { status: 'MIGRATED' };
      if (practiceFilter) params.practiceId = practiceFilter;
      const listRes = await api.migrationList(params);
      migratedIds = (listRes.success ? listRes.data.records : []).map(r => r.id);
    }
    if (migratedIds.length === 0) {
      setExecuteResult({ error: practiceFilter
        ? 'No migrated records for the selected practice'
        : 'No migrated records to re-merge' });
      return;
    }
    if (!window.confirm(
      `Re-merge ${migratedIds.length} MIGRATED record${migratedIds.length === 1 ? '' : 's'}?\n\n` +
      `This re-runs the merge step against the existing V2 form. V1 answers will overwrite V2 ` +
      `for any question where both have a value. Use this to fix V2 forms produced by the old ` +
      `broken merge — do NOT use after patients/staff have started filling out the V2 form.`
    )) return;
    setRemerging(true);
    setExecuteResult(null);
    try {
      const res = await api.migrationRemerge(migratedIds);
      setExecuteResult(res.success ? res.data : { error: res.error });
      setSelected(new Set());
      await loadData();
    } catch (err) {
      setExecuteResult({ error: err.message });
    }
    setRemerging(false);
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    // Only MATCHED rows can be approved — they have v2_form_id / v2_patient_id
    // / v2_appointment_id resolved. PENDING rows are unmatched (no V2 form to
    // merge into) so approving them just sets us up for "No V2 form found"
    // failures during execute. PENDING rows need manual-match (or reject) first.
    const selectable = records.filter(r => r.status === 'MATCHED');
    setSelected(new Set(selectable.map(r => r.id)));
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>
        V1 → V2 Migration Review
      </h1>
      <p style={{ color: '#6b7280', marginBottom: '24px' }}>
        Scan V1 forms, review matches, approve, and migrate to V2.
      </p>

      {/* Practice filter — drives both the table and which APPROVED forms get migrated */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
        <label style={{ fontWeight: 600, color: '#374151' }}>Practice:</label>
        <select
          value={practiceFilter}
          onChange={(e) => setPracticeFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', minWidth: '240px' }}
        >
          <option value="">All practices</option>
          {practices.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        {practiceFilter && (
          <span style={{ fontSize: '12px', color: '#6b7280' }}>
            Migrate-Approved will only execute records matched to this practice.
          </span>
        )}
      </div>

      {/* Stats bar */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {Object.entries(counts).filter(([k]) => k !== 'total').map(([status, count]) => (
          <div
            key={status}
            onClick={() => setFilter(filter === status ? '' : status)}
            style={{
              padding: '12px 20px', borderRadius: '8px', cursor: 'pointer',
              background: filter === status ? STATUS_COLORS[status] : '#f3f4f6',
              color: filter === status ? '#fff' : '#374151',
              fontWeight: 600, minWidth: '100px', textAlign: 'center',
              border: `2px solid ${STATUS_COLORS[status] || '#e5e7eb'}`,
            }}
          >
            <div style={{ fontSize: '24px' }}>{count}</div>
            <div style={{ fontSize: '12px', textTransform: 'uppercase' }}>{status}</div>
          </div>
        ))}
        {counts.total > 0 && (
          <div style={{ padding: '12px 20px', borderRadius: '8px', background: '#1f2937', color: '#fff', fontWeight: 600, minWidth: '100px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px' }}>{counts.total}</div>
            <div style={{ fontSize: '12px' }}>TOTAL</div>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <button onClick={handleScan} disabled={scanning}
          style={{ padding: '10px 20px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: scanning ? 'wait' : 'pointer', opacity: scanning ? 0.6 : 1 }}>
          {scanning ? 'Scanning...' : 'Scan V1 Forms'}
        </button>
        <button onClick={selectAllVisible} disabled={records.length === 0}
          style={{ padding: '10px 20px', background: '#6b7280', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' }}>
          Select All Matchable
        </button>
        <button onClick={handleApprove} disabled={selected.size === 0}
          style={{ padding: '10px 20px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', opacity: selected.size === 0 ? 0.4 : 1 }}>
          Approve Selected ({selected.size})
        </button>
        <button onClick={handleReject} disabled={selected.size === 0}
          style={{ padding: '10px 20px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', opacity: selected.size === 0 ? 0.4 : 1 }}>
          Reject Selected
        </button>
        <button onClick={handleExecute} disabled={executing || (counts.APPROVED || 0) === 0}
          style={{ padding: '10px 20px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: executing ? 'wait' : 'pointer', opacity: executing || (counts.APPROVED || 0) === 0 ? 0.4 : 1 }}>
          {executing ? 'Migrating...' : `Migrate Approved (${counts.APPROVED || 0})`}
        </button>
        <button onClick={handleRetry} disabled={retrying || (counts.FAILED || 0) === 0}
          title="Re-run migration for FAILED rows. Use after fixing the underlying cause (mapping, sync, etc.)."
          style={{ padding: '10px 20px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: retrying ? 'wait' : 'pointer', opacity: retrying || (counts.FAILED || 0) === 0 ? 0.4 : 1 }}>
          {retrying ? 'Retrying...' : `Retry Failed (${counts.FAILED || 0})`}
        </button>
        <button onClick={handleRemerge} disabled={remerging || (counts.MIGRATED || 0) === 0}
          title="Re-run the merge step against MIGRATED rows. Use to fix V2 forms produced by the old broken merge. Destructive: overwrites V2 answers where V1 also has a value — do NOT use after patients have started filling out the form."
          style={{ padding: '10px 20px', background: '#0891b2', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: remerging ? 'wait' : 'pointer', opacity: remerging || (counts.MIGRATED || 0) === 0 ? 0.4 : 1 }}>
          {remerging ? 'Re-merging...' : `Re-merge MIGRATED (${counts.MIGRATED || 0})`}
        </button>
      </div>

      {/* Scan result */}
      {scanResult && (() => {
        const isError = !!scanResult.error;
        const isRunning = scanResult.status === 'running';
        const bg = isError ? '#fef2f2' : isRunning ? '#eff6ff' : '#f0fdf4';
        const border = isError ? '#fecaca' : isRunning ? '#bfdbfe' : '#bbf7d0';
        const fg = isError ? '#dc2626' : isRunning ? '#1e40af' : '#166534';
        return (
          <div style={{ padding: '12px 16px', marginBottom: '16px', borderRadius: '6px', background: bg, border: `1px solid ${border}` }}>
            <span style={{ color: fg }}>
              {isError && `Scan failed: ${scanResult.error}`}
              {isRunning && `Scanning V1 forms… ${scanResult.elapsedSeconds ? `(~${Math.round(scanResult.elapsedSeconds)}s elapsed)` : ''} Safe to leave this page open — we're polling in the background.`}
              {!isError && !isRunning && (
                <>Scanned {scanResult.total_scanned} forms, stored {scanResult.stored}, auto-matched {scanResult.matched}{scanResult.skipped_past ? `, skipped ${scanResult.skipped_past} past appointments` : ''}{scanResult.adhoc_skipped ? `, ${scanResult.adhoc_skipped} V1 ad-hoc forms left out (not migrated)` : ''}</>
              )}
            </span>
            {/* commons #228: V1 ad-hoc forms are not migrated — the practice re-sends them from V2 */}
            {!isError && !isRunning && Array.isArray(scanResult.adhoc_pending) && scanResult.adhoc_pending.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 6 }}>
                  V1 ad-hoc forms to re-send from V2 before cutover ({scanResult.adhoc_pending.length}) — these are not migrated; the old link will show "This form has moved".
                </div>
                <table className="table" style={{ fontSize: 13 }}>
                  <thead><tr><th>Patient</th><th>Appointment</th><th>Type</th><th>V1 status</th><th>Answered</th><th>V1 appt id</th></tr></thead>
                  <tbody>
                    {scanResult.adhoc_pending.map((r) => (
                      <tr key={`${r.appointment_id}-${r.checkin_id}`}>
                        <td>{r.patient_name || '—'}</td>
                        <td>{r.appointment_date ? String(r.appointment_date).slice(0, 16).replace('T', ' ') : '—'}</td>
                        <td>{r.appointment_type || '—'}</td>
                        <td>{r.status || '—'}</td>
                        <td>{r.questions_answered != null && r.total_questions != null ? `${r.questions_answered}/${r.total_questions}` : '—'}</td>
                        <td style={{ fontFamily: 'ui-monospace, monospace' }}>{r.appointment_id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* Execute result — surface per-row errors so admin knows WHY rows failed,
          not just "0 succeeded, 3 failed" with no actionable detail. */}
      {executeResult && (
        <div style={{ padding: '12px 16px', marginBottom: '16px', borderRadius: '6px', background: (executeResult.error || executeResult.failed > 0) ? '#fef2f2' : '#f0fdf4', border: `1px solid ${(executeResult.error || executeResult.failed > 0) ? '#fecaca' : '#bbf7d0'}` }}>
          {executeResult.error ? (
            <span style={{ color: '#dc2626' }}>Execute failed: {executeResult.error}</span>
          ) : (
            <div>
              <div style={{ color: executeResult.failed > 0 ? '#dc2626' : '#166534', fontWeight: 600 }}>
                Migrated: {executeResult.succeeded} succeeded, {executeResult.failed} failed
              </div>
              {executeResult.failed > 0 && executeResult.results && (
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#991b1b' }}>
                  <div style={{ fontWeight: 500, marginBottom: '4px' }}>Failure reasons:</div>
                  {/* Aggregate identical errors so 50 "No V2 form found" rows render once. */}
                  {Object.entries(
                    executeResult.results
                      .filter(r => !r.success)
                      .reduce((acc, r) => { acc[r.error] = (acc[r.error] || 0) + 1; return acc; }, {})
                  ).map(([msg, count]) => (
                    <div key={msg}>• {msg} <span style={{ color: '#6b7280' }}>(×{count})</span></div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '10px', textAlign: 'left', width: '40px' }}></th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Patient</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>DOB</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Practice</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Appointment</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>V1 Progress</th>
              <th style={{ padding: '10px', textAlign: 'center' }}>Confidence</th>
              <th style={{ padding: '10px', textAlign: 'center' }}>Method</th>
              <th style={{ padding: '10px', textAlign: 'center' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>
            ) : records.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                {filter ? `No ${filter} records` : 'No records. Click "Scan V1 Forms" to start.'}
              </td></tr>
            ) : records.map(r => (
              <tr key={r.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '10px' }}>
                  {/* Only MATCHED rows can be approved (they have v2_* resolved).
                      PENDING rows still get a checkbox so admin can reject them. */}
                  {['PENDING', 'MATCHED'].includes(r.status) && (
                    <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)}
                      title={r.status === 'PENDING' ? 'Unmatched — Reject only (manual match required to approve)' : ''} />
                  )}
                </td>
                <td style={{ padding: '10px', fontWeight: 500 }}>{r.patient_name || '—'}</td>
                <td style={{ padding: '10px' }}>{r.dob || '—'}</td>
                <td style={{ padding: '10px', fontSize: '13px' }}>
                  {r.v2_practice_name || (r.v2_practice_id ? `#${r.v2_practice_id}` : <span style={{ color: '#9ca3af' }}>unmatched</span>)}
                </td>
                <td style={{ padding: '10px' }}>
                  <div>{r.appointment_date ? r.appointment_date.split('T')[0] : '—'}</div>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>{r.appointment_type || ''}</div>
                </td>
                <td style={{ padding: '10px' }}>
                  {r.v1_questions_answered}/{r.v1_total_questions}
                </td>
                <td style={{ padding: '10px', textAlign: 'center' }}>
                  {r.match_confidence > 0 && (
                    <span style={{ fontWeight: 600, color: r.match_confidence >= 90 ? '#10b981' : r.match_confidence >= 70 ? '#f59e0b' : '#ef4444' }}>
                      {r.match_confidence}%
                    </span>
                  )}
                </td>
                <td style={{ padding: '10px', textAlign: 'center', fontSize: '12px', color: '#6b7280' }}>
                  {r.match_method || '—'}
                </td>
                <td style={{ padding: '10px', textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
                    background: `${STATUS_COLORS[r.status]}20`, color: STATUS_COLORS[r.status]
                  }}>
                    {r.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
